import React, { ChangeEvent, InvalidEvent, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { postFindUsernameAPI } from 'apis/auth/authAPI';
import { isValidateEmail, isValidPhoneNumber, formatPhoneNumber } from 'constants/regularExpression';
import { useNavigate } from 'react-router-dom';
import './FindPopup.css';
import { useTranslation } from 'react-i18next';

interface FindIdPopupProps {
  isOpen: boolean;
  handleClose: () => void;
  handleOpenPwPopup: () => void; // 비밀번호 팝업 오픈 핸들러 prop 추가
}

export default function FindIdPopup({ isOpen, handleClose, handleOpenPwPopup }: FindIdPopupProps) {
  const [step, setStep] = useState<'form' | 'result'>('form');

  // 사용자의 내/외국인 타입 상태 추가 (이메일, 전화번호 인증버튼에 필요)
  const [type, setType] = useState('NATIVE');
  const [name, setName] = useState(''); // 이름 상태
  // 휴대폰번호
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberAlert, setPhoneNumberAlert] = useState(true); // 휴대폰번호 유효성 상태 (기본값 true)
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false); // 사용자가 휴대폰번호 필드를 벗어났는지 여부
  // 아메알
  const [email, setEmail] = useState(''); // 이메일 상태
  const [emailAlert, setEmailAlert] = useState(true); // 이메일 유효성 상태 (기본값 true)
  const [emailTouched, setEmailTouched] = useState(false); // 사용자가 이메일 필드를 벗어났는지 여부
  const [errormsg, setErrormsg] = useState(''); // 에러메세지 상태
  const navigate = useNavigate();
  // 응답받은 id
  const [id, setId] = useState<string | null>(null); // id 상태로 저장
  // 다국어
  const { t } = useTranslation();

  // 확인 버튼 클릭 핸들러
  const handleConfirm = async () => {
    const data = {
      type,
      name,
      ...(type === 'NATIVE'
        ? { phoneNumber } // 내국인일 경우
        : { email }) // 외국인일 경우
    };

    try {
      // API
      const result = await postFindUsernameAPI(data); // API 호출

      if (result.data) {
        const id = result.data.data.username; // API로부터 받은 id(username)
        setId(id); // 상태에 저장
        setStep('result'); // 다음 스텝으로 전환
      }
    } catch (error: any) {
      // 에러났을때 메세지 Alert
      const errorMessage = error?.data.message || t('find_id_page.placeholder.error');
      setErrormsg(errorMessage);
    }
  };
  // 핸드폰 번호
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = event.target.value?.replace(/[^0-9]/g, '');
    setPhoneNumber(formatPhoneNumber(phoneNumber));
  };

  const handlePhoneNumberBlur = () => {
    setPhoneNumberTouched(true); // 사용자가 필드를 떠났다는 표시
    setPhoneNumberAlert(isValidPhoneNumber(phoneNumber)); // 유효성 검사를 실행
  };

  // 이메일 필드에서 포커스를 벗어날 때 호출되는 함수
  const handleEmailBlur = () => {
    setEmailTouched(true); // 사용자가 필드를 떠났다는 표시
    setEmailAlert(isValidateEmail(email)); // 유효성 검사를 실행
  };
  // 모달 닫기와 함께 모든 상태를 리셋
  const handleCloseAndReset = () => {
    // 모든 상태를 초기값으로 리셋
    setStep('form'); // step 상태를 'form'으로 초기화
    setType('NATIVE');
    setName('');
    setPhoneNumber('');
    setPhoneNumberAlert(true);
    setPhoneNumberTouched(false);
    setEmail('');
    setEmailAlert(true);
    setEmailTouched(false);
    setErrormsg('');
    setId(null);
    handleClose(); // 부모로부터 받은 handleClose 호출
  };

  const handleOpenPwPopupAndClose = () => {
    handleCloseAndReset(); // 현재 팝업 닫기
    handleOpenPwPopup(); // 비밀번호 재설정 팝업 열기
  };

  // 입력값이 유효하지 않을 경우 호출되는 함수 (커스텀 에러 메시지 설정)
  const handleInvalid = (e: InvalidEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(t('validation.required')); // 다국어 에러 메시지 설정
  };

  // 사용자가 입력을 시작하면 호출되는 함수 (에러 메시지 제거 함수)
  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.setCustomValidity(''); // 에러 메시지를 초기화
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseAndReset} transitionDuration={0}>
      <DialogTitle className="headerTitle">
        <span>{t('find_id_page.title')}</span>
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ width: '400px', maxWidth: '400px', paddingY: '20px !important' }}>
        {step === 'form' && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_id_page.type')}
              </Typography>
              <RadioGroup
                row
                value={type} // userType 상태 사용
                onChange={(e) => setType(e.target.value)} // 상태 업데이트
                name="type"
                sx={{ height: 32 }}
              >
                <FormControlLabel
                  value="NATIVE"
                  control={<Radio size="small" sx={{ height: 32 }} />}
                  label={t('find_id_page.phone')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
                <FormControlLabel
                  value="FOREIGNER"
                  control={<Radio size="small" sx={{ height: 32 }} />}
                  label={t('find_id_page.email')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </RadioGroup>
            </Box>

            <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
              <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                {t('find_id_page.name')}
              </Typography>
              <TextField
                required
                name="name"
                size="small"
                onInvalid={handleInvalid}
                onInput={handleInput}
                placeholder={t('find_id_page.placeholder.name')}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  flexGrow: 1,
                  width: '100px',
                  '& .MuiInputBase-root': {
                    // 내부의 input 요소에 스타일 적용
                    height: '32px',
                    borderRadius: '4px',
                    '& input': {
                      height: '32px',
                      fontSize: '0.75rem',
                      padding: '0 8px'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginX: '8px'
                  }
                }}
              />
            </Box>

            {type === 'NATIVE' ? (
              // 내국인일 경우 휴대전화 입력 폼
              <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
                <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                  {t('find_id_page.phone')}
                </Typography>
                <TextField
                  required
                  onInvalid={handleInvalid}
                  onInput={handleInput}
                  name="phoneNumber"
                  id="phoneNumber"
                  type="tel"
                  size="small"
                  placeholder={t('find_id_page.placeholder.phone')}
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  onBlur={handlePhoneNumberBlur} // 포커스를 벗어날 때 유효성 검사
                  error={phoneNumberTouched && !phoneNumberAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={phoneNumberTouched && !phoneNumberAlert ? t('find_id_page.placeholder.phone_wrong') : null} // 오류 메시지
                  inputProps={{
                    inputMode: 'numeric', // 모바일에서 숫자키패드
                    pattern: '[0-9]*' // 숫자만 입력
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.75rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
            ) : (
              // 외국인일 경우 이메일 입력 폼
              <Box display="flex" flexDirection="row" alignItems="start" gap={1}>
                <Typography width={90} variant="caption" color="#7f7f7f" height={32} lineHeight={2.75}>
                  {t('find_id_page.email')}
                </Typography>
                <TextField
                  required
                  onInvalid={handleInvalid}
                  onInput={handleInput}
                  name="email"
                  id="email"
                  type="email"
                  size="small"
                  placeholder={t('find_id_page.placeholder.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)} // 이메일 상태 업데이트
                  onBlur={handleEmailBlur} // 포커스를 벗어날 때 유효성 검사
                  error={emailTouched && !emailAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={emailTouched && !emailAlert ? t('find_id_page.placeholder.email_wrong') : null} // 오류 메시지
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.75rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
            )}

            {errormsg && (
              <Alert severity="warning" sx={{ paddingY: '2px' }}>
                {errormsg}
              </Alert>
            )}
          </Box>
        )}
        {step === 'result' && (
          // 아이디 결과 화면
          <Box sx={{ textAlign: 'center', margin: '20px 0' }}>
            <Typography gutterBottom>
              {name}
              {t('find_id_page.content1')}
            </Typography>
            <Typography gutterBottom sx={{ fontWeight: 'bold', fontSize: '20px' }}>
              {id}
            </Typography>
            <Typography gutterBottom>{t('find_id_page.content2')}</Typography>
          </Box>
        )}
      </DialogContent>
      {/* 확인 버튼 */}
      <DialogActions sx={{ px: '24px', pb: '20px', pt: 0 }}>
        {step === 'form' && (
          <Button variant="contained" color="primary" size="medium" fullWidth onClick={handleConfirm} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
            {t('find_id_page.btn.ok')}
          </Button>
        )}
        {step === 'result' && (
          <>
            <Button variant="contained" color="primary" fullWidth onClick={handleCloseAndReset} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
              {t('find_id_page.btn.log_in')}
            </Button>
            <Button variant="outlined" color="primary" fullWidth onClick={handleOpenPwPopupAndClose} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>
              {t('find_id_page.btn.reset_password')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
